/**
 * Sidebar tab groups for Recommendations and Amendments wizards.
 * Navigation `activeSection` holds the tab group id (e.g. `insurance`, `super`).
 */

export const RECOMMENDATIONS_TAB_GROUPS = [
  {
    id: 'insurance',
    title: 'Insurance',
    sectionIds: [
      'recommendedInsurance',
      'quotingOptions',
      'alternativeInsurance',
    ],
  },
  {
    id: 'super',
    title: 'Super',
    sectionIds: [
      'clientSuperGoals',
      'currentPortfolio',
      'alternativePortfolio',
      'likeForLikePortfolio',
      'projectionCharts',
    ],
  },
  {
    id: 'contributions',
    title: 'Contributions',
    sectionIds: ['recommendedContributions'],
  },
  {
    id: 'ttr',
    title: 'TTR',
    sectionIds: ['ttrStrategy'],
  },
  {
    id: 'debt',
    title: 'Debt',
    sectionIds: ['pporLoanPayoff'],
  },
  {
    id: 'fhss',
    title: 'FHSS',
    sectionIds: ['fhss'],
  },
  {
    id: 'other',
    title: 'Other',
    sectionIds: ['ebaArrangements', 'adviceFees'],
  },
];

export const AMENDMENTS_TAB_GROUPS = [
  {
    id: 'insurance',
    title: 'Insurance',
    sectionIds: [
      'recommendedInsurance',
      'quotingOptions',
      'alternativeInsurance',
    ],
  },
  {
    id: 'super',
    title: 'Super',
    sectionIds: ['clientSuperGoals', 'currentPortfolio', 'projectionCharts'],
  },
];

export const RECOMMENDATIONS_NAV_TABS = RECOMMENDATIONS_TAB_GROUPS.map(({ id, title }) => ({
  id,
  title,
}));

export const AMENDMENTS_NAV_TABS = AMENDMENTS_TAB_GROUPS.map(({ id, title }) => ({
  id,
  title,
}));

export const ADVICE_TAB_GROUP_PAGE_IDS = new Set(['advice', 'amendments']);

export function isAdviceTabGroupPage(tabName) {
  return ADVICE_TAB_GROUP_PAGE_IDS.has(tabName);
}

export function getTabGroupsForPage(tabName) {
  if (tabName === 'advice') return RECOMMENDATIONS_TAB_GROUPS;
  if (tabName === 'amendments') return AMENDMENTS_TAB_GROUPS;
  return [];
}

export function getNavTabsForPage(tabName) {
  if (tabName === 'advice') return RECOMMENDATIONS_NAV_TABS;
  if (tabName === 'amendments') return AMENDMENTS_NAV_TABS;
  return [];
}

export function getDefaultTabIdForPage(tabName) {
  const tabs = getNavTabsForPage(tabName);
  return tabs[0]?.id ?? null;
}

/**
 * Mirror wizard layout: the outcome tiles switch tabs, so the sidebar shows
 * Recommendations and Amendments without their sub-items. Page and tab ids are unchanged.
 */
export function withAdviceTabSectionsHidden(navTree, hidden) {
  if (!hidden || !Array.isArray(navTree)) return navTree;
  return navTree.map((page) => (
    page && ADVICE_TAB_GROUP_PAGE_IDS.has(page.tabName) && Array.isArray(page.sections) && page.sections.length
      ? { ...page, sections: [] }
      : page
  ));
}

export function getTabGroupById(tabGroups, tabId) {
  return tabGroups.find((group) => group.id === tabId) ?? tabGroups[0] ?? null;
}

export function getTabTitle(tabName, tabId) {
  const tabs = getNavTabsForPage(tabName);
  const tab = tabs.find((entry) => entry.id === tabId);
  return tab?.title ?? tabId;
}

/** Ordered sections limited to one sidebar tab group. */
export function filterSectionsForTabGroup(orderedSections, tabGroup) {
  if (!tabGroup) return [];
  const allowed = new Set(tabGroup.sectionIds);
  const orderIndex = new Map(tabGroup.sectionIds.map((id, index) => [id, index]));
  return (orderedSections || [])
    .filter((section) => allowed.has(section.id))
    .sort((a, b) => (orderIndex.get(a.id) ?? 0) - (orderIndex.get(b.id) ?? 0));
}
